import { z } from 'zod/v4'
import { formatBytes } from '@/utils/bytes'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phoneRegex = /^\+?\d{6,15}$/ // generic international phone number

export const EmailOrPhoneSchema = z
  .string()
  .nonempty('Please enter a valid email or phone number')
  .refine((val) => emailRegex.test(val) || phoneRegex.test(val), {
    message: 'Please enter a valid email or phone number',
  })

export const PhoneNumberSchema = z
  .string()
  .trim()
  .min(7, 'Phone number too short')
  .max(20, 'Phone number too long')
  .refine((val) => /^\+?[0-9\s\-().]{7,20}$/.test(val), {
    message: 'Invalid phone number format',
  })

export const StrongPasswordSchema = z
  .string()
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/,
    'Password must be 8-20 characters long and include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.',
  )
export const PasswordConfirmationSchema = z
  .string()
  .nonempty('Password confirmation is required')

export const UploadFileSchema = (options: {
  maxSize: number
  allowedMimes: Array<string>
  allowedExtensions: Array<string>
  message?: string
}) =>
  z
    .instanceof(File)
    .refine((file) => file.size <= options.maxSize, {
      message: `File size exceeds the ${formatBytes(options.maxSize)} limit. Please upload a smaller file.`,
    })
    .refine(
      (file) =>
        options.allowedMimes.includes(file.type) ||
        options.allowedExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext),
        ),
      {
        message:
          options.message ??
          'Invalid file type. Allowed: PDF, AI, SVG, PNG, JPG, JPEG.',
      },
    )

const MAX_SIZE = 5 * 1024 * 1024 // 5 MB in bytes
const ACCEPTED_MIME = [
  'application/pdf',
  'image/svg+xml',
  'image/png',
  'image/jpeg',
  // note: "image/jpg" is not an official MIME, jpeg covers it
]
const ACCEPTED_EXT = ['pdf', 'ai', 'svg', 'png', 'jpg', 'jpeg']

export const FileInputSchema = z
  .any()
  .refine((files) => !!files && files.length === 1, {
    message: 'Please upload exactly one file.',
  })
  .superRefine((files, ctx) => {
    const file = files && files[0]
    if (!file) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'File is required.',
      })
      return
    }

    if (file.size > MAX_SIZE) {
      ctx.addIssue({
        code: 'too_big',
        maximum: MAX_SIZE,
        type: 'number',
        inclusive: true,
        input: file,
        origin: 'file',
        message: 'File must be 5 MB or smaller.',
      })
    }

    const mimeOk = ACCEPTED_MIME.includes(file.type)

    const ext = (file.name?.split('.').pop() || '').toLowerCase()
    const extOk = ACCEPTED_EXT.includes(ext)

    if (!mimeOk && !extOk) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Unsupported file type. Allowed: PDF, AI, SVG, PNG, JPG, JPEG.',
      })
    }
  })
